import React, { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, Clock, CheckCircle2, Loader2, ThumbsUp, Ban } from 'lucide-react';
import { commissionService } from '../../services/commissionService';
import { useAuth } from '../../context/AuthContext';
import StatusBadge from '../../components/StatusBadge';

const formatMoney = (value, currency) => {
  const num = Number(value || 0).toLocaleString();
  if (!currency) return num;
  const symbols = { PKR: 'PKR ', USD: '$ ', EUR: '€ ', GBP: '£ ', AED: 'AED ' };
  return (symbols[currency] || currency + ' ') + num;
};

const SummaryCard = ({ label, value, icon, currency }) => (
  <div className="bg-white rounded-card shadow-soft border border-gray-100 p-4">
    <div className="flex items-center justify-between">
      <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">{label}</p>
      {icon}
    </div>
    <p className="text-xl font-semibold text-gray-900 mt-2">{formatMoney(value, currency)}</p>
    <div className="h-1 w-10 bg-primary rounded-full mt-3"></div>
  </div>
);

const Commissions = () => {
  const { user } = useAuth();

  const canApprove = user && ['super_admin', 'finance_manager'].includes(user.role);
  const isManagerView = user && ['super_admin', 'finance_manager', 'sales_manager'].includes(user.role);

  const [summary, setSummary] = useState(null);
  const [rankings, setRankings] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [busyKey, setBusyKey] = useState('');

  const loadAll = async () => {
    setLoading(true);
    setError('');
    try {
      const summaryRes = await commissionService.getSummary();
      setSummary(summaryRes.data);

      const commissionsRes = await commissionService.getCommissions(statusFilter || undefined);
      setCommissions(commissionsRes.data || []);

      if (isManagerView) {
        const rankingsRes = await commissionService.getRankings();
        setRankings(rankingsRes.data || []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    if (successMsg) {
      const t = setTimeout(() => setSuccessMsg(''), 3000);
      return () => clearTimeout(t);
    }
  }, [successMsg]);

  const handleApproveOverall = async (commissionId) => {
    setBusyKey(commissionId + '-overall');
    setActionError('');
    try {
      await commissionService.approveCommission(commissionId);
      setSuccessMsg('Commission approved successfully!');
      await loadAll();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setBusyKey('');
    }
  };

  const handleHoldOverall = async (commissionId) => {
    setBusyKey(commissionId + '-hold');
    setActionError('');
    try {
      await commissionService.holdCommission(commissionId);
      setSuccessMsg('Commission put on hold.');
      await loadAll();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setBusyKey('');
    }
  };

  const handleApprove = async (commissionId, milestoneId) => {
    const key = commissionId + '-' + milestoneId;
    setBusyKey(key);
    setActionError('');
    try {
      await commissionService.approveMilestone(commissionId, milestoneId);
      setSuccessMsg('Milestone approved!');
      await loadAll();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setBusyKey('');
    }
  };

  const handleReverse = async (commissionId, milestoneId) => {
    const key = commissionId + '-' + milestoneId + '-reverse';
    setBusyKey(key);
    setActionError('');
    try {
      await commissionService.reverseMilestone(commissionId, milestoneId);
      setSuccessMsg('Milestone reversed.');
      await loadAll();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setBusyKey('');
    }
  };

  const byCurrency = summary?.by_currency && summary.by_currency.length > 0
    ? summary.by_currency
    : (summary ? [summary] : []);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-gray-900">Commissions</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          {isManagerView ? 'Team commission tracking and approvals' : 'Your commission earnings'}
        </p>
      </div>

      {error && (
        <div className="mb-4 px-4 py-2.5 rounded-control bg-red-50 text-red-700 text-sm">
          {error}
        </div>
      )}

      {successMsg && (
        <div className="mb-4 px-4 py-2.5 rounded-control bg-green-50 text-green-700 text-sm font-medium">
          {successMsg}
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-20 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      )}

      {!loading && byCurrency.length > 0 && (
        <div className="space-y-3 mb-6">
          {byCurrency.map((s) => (
            <div key={s.currency}>
              {byCurrency.length > 1 && (
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">{s.currency} deals</p>
              )}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <SummaryCard label="Pending" value={s.total_pending} currency={s.currency} icon={<Clock className="w-4 h-4 text-amber-400" />} />
                <SummaryCard label="Approved" value={s.total_approved} currency={s.currency} icon={<CheckCircle2 className="w-4 h-4 text-blue-400" />} />
                <SummaryCard label="Paid" value={s.total_paid} currency={s.currency} icon={<DollarSign className="w-4 h-4 text-green-500" />} />
                <SummaryCard label="Total Liability" value={s.total_liability} currency={s.currency} icon={<TrendingUp className="w-4 h-4 text-primary-light" />} />
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && isManagerView && rankings.length > 0 && (
        <div className="bg-white rounded-card shadow-soft border border-gray-100 p-5 mb-6">
          <h2 className="text-sm font-semibold text-gray-800 mb-4">Rep Rankings</h2>
          <div className="space-y-3">
            {rankings.map((r, index) => (
              <div key={r.sales_rep_id + '-' + r.currency} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 flex items-center justify-center rounded-full bg-surface-subtle text-xs font-medium text-gray-500">
                    {index + 1}
                  </span>
                  <div>
                    <p className="text-sm font-medium text-gray-800">{r.sales_rep_name}</p>
                    <p className="text-xs text-gray-400">{r.deal_count} deals</p>
                  </div>
                </div>
                <p className="text-sm font-semibold text-gray-800">{formatMoney(r.total_calculated, r.currency)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && (
        <div className="bg-white rounded-card shadow-soft border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4 gap-3">
            <h2 className="text-sm font-semibold text-gray-800">Commission Ledger</h2>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 rounded-control border border-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-primary-light"
            >
              <option value="">All Statuses</option>
              <option value="Pending">Pending</option>
              <option value="Approved">Approved</option>
              <option value="Paid">Paid</option>
              <option value="On Hold">On Hold</option>
              <option value="Cancelled">Cancelled</option>
              <option value="Reversed">Reversed</option>
            </select>
          </div>

          {actionError && (
            <div className="mb-3 px-4 py-2.5 rounded-control bg-red-50 text-red-700 text-sm">
              {actionError}
            </div>
          )}

          {commissions.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-10">No commission records found.</p>
          )}

          <div className="space-y-4">
            {commissions.map((c) => (
              <div key={c.commission_id || c.id} className="border border-gray-100 rounded-control p-4">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <p className="text-sm font-medium text-gray-800">{c.sales_rep_name}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {c.contract_id ? 'Contract ' + c.contract_id : 'Lead ' + c.lead_id}
                      {' '}&middot; Rate {c.rate}% &middot; Total{' '}
                      <span className="font-semibold text-gray-700">
                        {formatMoney(c.total_commission_calculated, c.currency)}
                      </span>
                    </p>
                  </div>
                  <StatusBadge status={c.overall_status} />
                </div>

                {/* Overall approve/hold — jab milestone_payouts empty ho */}
                {canApprove && (!c.milestone_payouts || c.milestone_payouts.length === 0) && (
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-50">
                    {c.overall_status === 'Pending' && (
                      <>
                        <button
                          onClick={() => handleApproveOverall(c.commission_id)}
                          disabled={busyKey === c.commission_id + '-overall'}
                          className="flex items-center gap-1.5 text-xs font-bold text-white bg-green-600 hover:bg-green-700 px-3 py-1.5 rounded-lg disabled:opacity-50"
                        >
                          {busyKey === c.commission_id + '-overall'
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <ThumbsUp className="w-3 h-3" />}
                          Approve
                        </button>
                        <button
                          onClick={() => handleHoldOverall(c.commission_id)}
                          disabled={busyKey === c.commission_id + '-hold'}
                          className="flex items-center gap-1.5 text-xs font-bold text-amber-700 bg-amber-50 hover:bg-amber-100 px-3 py-1.5 rounded-lg border border-amber-200 disabled:opacity-50"
                        >
                          {busyKey === c.commission_id + '-hold'
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <Ban className="w-3 h-3" />}
                          Put on Hold
                        </button>
                      </>
                    )}
                    {c.overall_status === 'On Hold' && (
                      <button
                        onClick={() => handleApproveOverall(c.commission_id)}
                        disabled={busyKey === c.commission_id + '-overall'}
                        className="flex items-center gap-1.5 text-xs font-bold text-white bg-green-600 hover:bg-green-700 px-3 py-1.5 rounded-lg disabled:opacity-50"
                      >
                        {busyKey === c.commission_id + '-overall'
                          ? <Loader2 className="w-3 h-3 animate-spin" />
                          : <ThumbsUp className="w-3 h-3" />}
                        Approve
                      </button>
                    )}
                    {c.overall_status === 'Approved' && (
                      <span className="text-xs text-green-600 font-bold flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Approved — awaiting payroll
                      </span>
                    )}
                  </div>
                )}

                {/* Milestone level payouts */}
                {c.milestone_payouts && c.milestone_payouts.length > 0 && (
                  <div className="space-y-2 mt-3">
                    {c.milestone_payouts.map((mp) => {
                      const approveKey = c.commission_id + '-' + mp.milestone_id;
                      const reverseKey = approveKey + '-reverse';
                      return (
                        <div
                          key={mp.milestone_id}
                          className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-surface-subtle rounded-control px-3 py-2.5"
                        >
                          <div className="flex items-center gap-3">
                            <StatusBadge status={mp.status} />
                            <span className="text-xs text-gray-600">
                              Milestone {mp.milestone_id} &middot; {formatMoney(mp.commission_share, c.currency)}
                            </span>
                          </div>

                          {canApprove && mp.status === 'Pending' && (
                            <button
                              onClick={() => handleApprove(c.commission_id, mp.milestone_id)}
                              disabled={busyKey === approveKey}
                              className="self-start sm:self-auto text-xs font-medium text-primary-light hover:underline disabled:opacity-50"
                            >
                              {busyKey === approveKey ? 'Approving...' : 'Approve'}
                            </button>
                          )}

                          {canApprove && (mp.status === 'Approved' || mp.status === 'Paid') && (
                            <button
                              onClick={() => handleReverse(c.commission_id, mp.milestone_id)}
                              disabled={busyKey === reverseKey}
                              className="self-start sm:self-auto text-xs font-medium text-red-500 hover:underline disabled:opacity-50"
                            >
                              {busyKey === reverseKey ? 'Reversing...' : 'Reverse'}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Commissions;